// components/FastIssuesView.js
import React, { useMemo, useState } from 'react';
import { List } from 'react-window';
import { useIssueFeed } from '../hooks/useIssueFeed';

const ROW_HEIGHT = 64;

// Toggle obfuscation on/off for screen recording
const ENABLE_OBFUSCATION = false;

/**
 * Obfuscate text by showing only first 3 and last 3 characters
 * @param {string} text - Text to obfuscate
 * @returns {string} Obfuscated text
 */
function obfuscate(text) {
  if (!ENABLE_OBFUSCATION) return text;
  if (!text || typeof text !== 'string') return text;
  if (text.length <= 6) return text; // Too short to obfuscate
  const first3 = text.substring(0, 3);
  const last3 = text.substring(text.length - 3);
  const middleLength = text.length - 6;
  const asterisks = '*'.repeat(middleLength);
  return `${first3}${asterisks}${last3}`;
}

/**
 * Fast-loading issues view with virtualization and search
 * @param {{user: any}} props
 */
export function FastIssuesView({ user }) {
  // Construct auth header like ChatWindow does
  const getAuthHeader = () => {
    if (user.useSession && user.sessionToken) {
      return `Session ${user.sessionToken}`;
    }
    return `Bearer ${user.idToken}`;
  };

  // Always fetch ALL issues (scope='all')
  const { issues, loading, refresh } = useIssueFeed('all', getAuthHeader());
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all'); // 'all', 'my', 'others'
  const [smartSearchIds, setSmartSearchIds] = useState(null); // null = not active, array = filtered IDs
  const [smartSearchLoading, setSmartSearchLoading] = useState(false);

  // Handle smart search
  const handleSmartSearch = async () => {
    if (!searchQuery.trim()) return;

    setSmartSearchLoading(true);
    try {
      const response = await fetch('/api/issues/smartSearch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({ searchText: searchQuery })
      });

      if (!response.ok) throw new Error('Smart search failed');

      const data = await response.json();
      setSmartSearchIds(data.issueIds);
      console.log('[FastIssuesView] Smart search found:', data.issueIds.length, 'issues');
    } catch (error) {
      console.error('[FastIssuesView] Smart search error:', error);
      alert('Smart search failed. Please try again.');
    } finally {
      setSmartSearchLoading(false);
    }
  };

  // Filter by scope and search
  const filtered = useMemo(() => {
    let scoped = issues;

    // Apply scope filter using backend-provided issueScope field
    if (scopeFilter === 'my') {
      scoped = issues.filter(issue => issue.issueScope === 'my');
    } else if (scopeFilter === 'others') {
      scoped = issues.filter(issue => issue.issueScope === 'others');
    }
    // 'all' = no filtering

    // Apply smart search filter if active
    if (smartSearchIds !== null) {
      const idSet = new Set(smartSearchIds);
      scoped = scoped.filter(issue => idSet.has(issue.id));
    } else {
      // Apply regular text search
      scoped = search(scoped, searchQuery);
    }

    return scoped;
  }, [issues, scopeFilter, searchQuery, smartSearchIds]);

  // Create row props object with filtered issues
  const rowProps = useMemo(() => ({ issues: filtered }), [filtered]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Search and controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px' }}>
        <select
          value={scopeFilter}
          onChange={e => setScopeFilter(e.target.value)}
          style={{
            border: '1px solid #ccc',
            padding: '8px 12px',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Issues</option>
          <option value="my">My Issues</option>
          <option value="others">Others' Issues</option>
        </select>
        <input
          type="text"
          placeholder="Search issues…"
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            // Clear smart search when typing
            if (smartSearchIds !== null) setSmartSearchIds(null);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && e.ctrlKey) {
              handleSmartSearch();
            }
          }}
          style={{
            border: '1px solid #ccc',
            padding: '8px 12px',
            borderRadius: '4px',
            flex: 1
          }}
        />
        {smartSearchIds !== null ? (
          <button
            onClick={() => setSmartSearchIds(null)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              background: '#666',
              color: '#fff',
              border: 'none',
              cursor: 'pointer'
            }}
            title="Clear AI search"
          >
            ✕
          </button>
        ) : (
          <button
            onClick={handleSmartSearch}
            disabled={!searchQuery.trim() || smartSearchLoading}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              background: smartSearchLoading ? '#ccc' : '#6366f1',
              color: '#fff',
              border: 'none',
              cursor: smartSearchLoading ? 'wait' : 'pointer',
              opacity: !searchQuery.trim() ? 0.5 : 1
            }}
            title="AI-powered smart search (Ctrl+Enter)"
          >
            {smartSearchLoading ? '⏳' : '✨'}
          </button>
        )}
        <button
          onClick={refresh}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            background: '#000',
            color: '#fff',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Refresh
        </button>
      </div>

      {/* Status info */}
      <div style={{ padding: '0 8px', fontSize: '14px', color: '#666' }}>
        {loading ? 'Loading…' : (
          <>
            {filtered.length} issues
            {smartSearchIds !== null && (
              <span style={{ marginLeft: '8px', color: '#6366f1', fontWeight: 500 }}>
                ✨ AI filtered
              </span>
            )}
          </>
        )}
      </div>

      {/* Virtualized list */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {filtered.length > 0 ? (
          <List
            rowCount={filtered.length}
            rowHeight={ROW_HEIGHT}
            rowComponent={IssueRowWrapper}
            rowProps={rowProps}
            defaultHeight={600}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            No issues found
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Wrapper component for row that receives index and rowProps
 */
function IssueRowWrapper({ index, style, issues }) {
  const issue = issues[index];
  return <IssueRow issue={issue} style={style} />;
}

/**
 * Individual issue row
 */
function IssueRow({ issue, style }) {
  if (!issue) return null;

  // Check if issue is Done
  const isDone = issue.statusCategory?.toLowerCase() === 'done' ||
                 issue.status?.toLowerCase().includes('done') ||
                 issue.status?.toLowerCase().includes('closed');

  const delayed = issue.sort.group === 0;

  let pillClass, label, bgColor, textColor;

  if (isDone) {
    // Done issues
    pillClass = 'done-pill';
    label = 'Done';
    bgColor = '#e8e8e8';
    textColor = '#666';
  } else if (delayed) {
    // Overdue issues
    pillClass = 'delayed-pill';
    label = `${issue.sort.primary}d overdue`;
    bgColor = '#fee';
    textColor = '#c00';
  } else {
    // Upcoming or no due date
    pillClass = 'upcoming-pill';
    label = isFinite(issue.sort.primary) ? `due in ${issue.sort.primary}d` : 'no due date';
    bgColor = '#efe';
    textColor = '#060';
  }

  return (
    <div
      style={{
        ...style,
        padding: '8px 12px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: isDone ? 0.6 : 1
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textDecoration: isDone ? 'line-through' : 'none'
        }}>
          {issue.key} — {obfuscate(issue.summary)}
        </div>
        <div style={{ fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {issue.project} · {issue.issuetype} · {issue.status} · {issue.priority}
        </div>
      </div>
      <span className={pillClass} style={{
        fontSize: '12px',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: bgColor,
        color: textColor,
        whiteSpace: 'nowrap',
        marginLeft: '8px'
      }}>
        {label}
      </span>
    </div>
  );
}

/**
 * Search function for issues
 */
function search(list, q) {
  if (!q) return list;
  const s = q.toLowerCase();
  return list.filter(it => {
    // Safely convert description to string (it might be an object)
    const desc = typeof it.description === 'string' ? it.description : '';

    return (
      it.key.toLowerCase().includes(s) ||
      it.summary.toLowerCase().includes(s) ||
      desc.toLowerCase().includes(s) ||
      it.labels?.some(l => l.toLowerCase().includes(s)) ||
      it.project.toLowerCase().includes(s) ||
      it.status.toLowerCase().includes(s) ||
      it.priority.toLowerCase().includes(s) ||
      (it.assignee?.display || '').toLowerCase().includes(s)
    );
  });
}
